import _ from "lodash";

export const mappingTypes = {
  "Default" : 0,
  "Fixed" : 1,
  "Concat" : 2,
  "CalculatedExpression" : 3,
}

export const cubeComponentTypes = {
  "Attribute" : 0,
  "Dimension" : 1,
  "Measure" : 2,
  "TimeDimension" : 3,
}

export const mappingTypeDefault = "Default";
export const mappingTypeFixed = "Fixed";
export const mappingTypeConcat = "Concat";
export const mappingTypeCalculated = "CalculatedExpression";

export const freeTextValue = "@FreeText@";


export const createMapping = (columnName, cubeComponentCode, cubeComponentType, mappingType, expression, tempTableName, columnAlias) => {
  return {
    ColumnName: columnName,
    ColumnAlias: columnAlias,
    CubeComponentCode: cubeComponentCode,
    CubeComponentType: cubeComponentType,
    MappingType: mappingType,
    Expression: expression,
    TempTable: tempTableName
  }
}

export const getCubeComponentType = (cubeComponents, cubeComponentCode) => {
  return (
    [
      ...cubeComponents.dimensions,
      ...cubeComponents.timeDimensions,
      ...cubeComponents.attributes,
      ...cubeComponents.measures
    ].filter(comp => comp.Code === cubeComponentCode)[0].cubeComponentType
  )
}

export const getExpression = (componentValueDefinition) => {
  const mappingValue = componentValueDefinition.mappingValue;
  if (mappingValue.mappingType === mappingTypeFixed) {
    return mappingValue.fixedValue;
  }

  return null;
}

export const getDefaultMapping = (cubeComponent) => {
  return (
    {
      ColumnName: null,
      CubeComponentCode: cubeComponent.Code,
      CubeComponentType: cubeComponent.cubeComponentType,
      cubeComponent: cubeComponent,
      mappingValue: {
        mappingType: mappingTypeFixed,
        fixedValue: "",
        concatenatedValues: [],
        calculatedValue: []
      }
    }
  );  
}

export const valueIsValid = (componentValueDefinition) => {
  const mappingValue = componentValueDefinition.mappingValue;  
  if (mappingValue.mappingType === mappingTypeFixed) {
    return (mappingValue.fixedValue || "").length > 0;
  } else if (mappingValue.mappingType === mappingTypeConcat) {
    return concatenatedValuesAreValid(mappingValue.concatenatedValues)
  } else if (mappingValue.mappingType === mappingTypeCalculated) {
    return calculatedValueIsValid(mappingValue.calculatedValue);
  }

  return false;
}

const concatenatedValuesAreValid = (concatenatedValues) => {
  const hasValues = (concatenatedValues || []).length > 0;
  return (hasValues && !concatenatedValues.some(
    v => v.name === freeTextValue && (v.text || "").length === 0
  ));
}

const calculatedValueIsValid = (calculatedValue) => {
  
  for(let i = 0; i < calculatedValue.blocks.length; i++) {
    const block = calculatedValue.blocks[i];
    if ((block.componentValue || "").length === 0) {
      return false;
    }
    for(let j = 0; j < block.conditions.length; j++) {
      if ((block.conditions[j].text || "").length === 0) {
        return false;
      }
    }
  }
  return calculatedValue.defaultValue.isMandatory ? (calculatedValue.defaultValue.text || "").length > 0 : true;
}

export const getMappingType = (componentValueDefinition) => {
  return componentValueDefinition.mappingValue.mappingType;
}

export const getComponentExpression = (mappingValue) => {

  if (mappingValue.mappingType === mappingTypeFixed) {
    return getFixedExpression(mappingValue);
  } else if (mappingValue.mappingType === mappingTypeConcat) {
    return getConcatenatedExpression(mappingValue.concatenatedValues);
  } else if (mappingValue.mappingType === mappingTypeCalculated) {
    return getCalculatedExpression(mappingValue.calculatedValue);
  }
  return null;
}

const getFixedExpression = (mappingValue) => {
  return {
    basicExp: [`'${mappingValue.fixedValue}'`],
    calcExp: null
  }
}

const getConcatenatedExpression = (concatenatedValues) => {
  const values = concatenatedValues.map(c => {
    if (c.name === freeTextValue ) {
      return `'${c.text}'`;
    } else {
      return `[${c.name}]`
    }
  });
  return {
    basicExp: values,
    calcExp: null
  }
}


const getCalculatedExpression = (calculatedValue) => {

  const expression = [];

  for(let i = 0; i < calculatedValue.blocks.length; i++) {
    const block = calculatedValue.blocks[i];
    const mappingFilter = {
        FiltersGroupAnd: null,
        FiltersGroupOr: null     
    };
    const filter = {};
    const filters = [];
    for(let j = 0; j < block.conditions.length; j++) {
      const condition = block.conditions[j];
      const filterObj = {
        ColumnName: condition.name,
        ColumnType: "string",
        WhereAndOr: calculatedValue.logicalOperator,
        Operator: "=",
        FilterValues:[condition.text]
      }
      filters.push(filterObj);      
    }
    filter[i] = filters;


    if (calculatedValue.logicalOperator === 'AND') {
      mappingFilter.FiltersGroupAnd = filter;
    } else {
      mappingFilter.FiltersGroupOr = filter;
    }


    expression.push(
      {
        Item1: mappingFilter,
        Item2: `'${block.componentValue}'`  
      }
    );
   
  }

  if (calculatedValue.defaultValue && (calculatedValue.defaultValue.text || "").length > 0) {
    expression.push(
      {
        Item1: null,
        Item2: `'${calculatedValue.defaultValue.text}'`  
      }
    );
  }

  return {
    basicExp: null,
    calcExp: expression
  }
}

export const getMappingExpression = (mapping) => {

  if (!mapping || !mapping.Expression) {
    return null;
  }

  const expression = mapping.Expression;

  if (mapping.MappingType === mappingTypeFixed || mapping.MappingType === mappingTypeConcat) {
    const basicExpression = expression.basicExp;
    if ((basicExpression || []).length === 1) {
      return basicExpression[0];
    } else if ((basicExpression || []).length > 1){
      return basicExpression.join(" + ")
    } 
  } else if (mapping.MappingType === mappingTypeCalculated) {
    return getCalculatedMappingExpression(expression.calcExp)
  }

  return "";
}

const getCalculatedMappingExpression = (expression) => {
  let expressionFilter = "";

  for(let i = 0; i < (expression || []).length; i++) {
    const tuple = expression[i];
    const blockValue = tuple.Item2;
    if (tuple.Item1) {
      const blockLogicalOperator = tuple.Item1.FiltersGroupAnd ? "AND" : "OR";
      const blockFilters = blockLogicalOperator === "AND" ? tuple.Item1.FiltersGroupAnd : tuple.Item1.FiltersGroupOr;      
      const blockKeys = blockFilters ? Object.keys(blockFilters) || [] : [];
      for(let j = 0; j < blockKeys.length; j++) {
        let blockStr = "";
        const blockKey = blockKeys[j];
        const conditionFilters = blockFilters[blockKey] || [];
        
        for(let k = 0; k < conditionFilters.length; k++) {
          const conditionFilter = conditionFilters[k];
          const conditionStr = `[${conditionFilter.ColumnName}] ${conditionFilter.Operator} '${conditionFilter.FilterValues[0]}'`
          if (blockStr === "") {
            blockStr = conditionStr;
          } else {
            blockStr += ` ${blockLogicalOperator} ${conditionStr}`;
          }
        }
        blockStr = ` WHEN ${blockStr} THEN ${blockValue}`;
        expressionFilter += blockStr;
      }
    } else {
      expressionFilter += ` ELSE ${blockValue}`;
    }    
  }

  expressionFilter = `CASE ${expressionFilter} END`

  return expressionFilter;
}

export const getComponentsFromMappedCubeComponents = (mappedCubeComponents, excludedComponents) => {

  const components = (mappedCubeComponents || []).filter(c => !excludedComponents.includes(c)).map(c => (
    {
      name: c,
      text: ""
    }));
   
  return components;
}

export const getComponentsFromCubeComponents = (cubeComponents, excludedComponents, addFreeText) => {

  const allowedComponetTypes = ["Attribute", "Dimension", "TimeDimension"];

  const components = (cubeComponents || [])
    .filter(c => allowedComponetTypes.includes(c.cubeComponentType))
    .filter(c => !excludedComponents.includes(c.Code)).map(c => (
      {
        name: c.Code,
        text: "",
        codelistCode: c.CodelistCode
      }));

  if (addFreeText) {
    components.push(
      {
        name: freeTextValue,
        text: "", 
        codelistCode: null       
      }
    );
  }
    
  return components;
}

export const getInitConcatenatedValue = (name) => {
  return ({
    name: name,
    text: "",
    idx: 0
    });
}

export const getInitialCondition = (name, idx) => {
  return ({
    name: name,
    text: "",
    idx: idx
  });
}

export const getInitialBlock = (name, idx) => {
  return ({
    idx: idx,
    conditions: [getInitialCondition(name, 0)],
    defaultValue: "",
    baseValue: ""
  });
}

export const getInitIffCondition = (idx) => {
  return ({
      functionName:'',
      componentName:'',
      selectDistinct: null,
      logicalOperator: "",
      operator: "",
      values: {},
      idx: idx
  })
}


export const getInitialIIFBlock = (idx) => {
  return ({
    componentY: "",
    componentX: "",
    componentZ: "",
    conditions:[getInitIffCondition(0)],
    blockIdx: idx
  });
}



export const getInitialCaseCondition = (idx) => {
  return ({
    functionName: "",
    componentName: "",
    operator: "",
    selectDistinct:null,
    values:{}, 
    idx: idx
  });
}

export const getInitialCaseBlock = (idx) => {
  return ({
    blockIdx: idx,
    logicalOperator:'',
    baseValue: '',
    conditions: [getInitialCaseCondition(0)],
  });
}

export const getInitCalculatedValue = (cubeComponents, name, cubeComponentCode) => {
  
  const block = getInitialBlock(name, 0);

  const cubeComponent = (cubeComponents || []).find(c => c.Code === cubeComponentCode);

  const calculatedValue = {
    logicalOperator : "AND",
    blocks: [block],
    defaultValue: {text: null, isMandatory:componentIsMandatory(cubeComponent)}
  }

  return calculatedValue;
}

export const componentIsMandatory = (cubeComponent) => {
  return cubeComponent.cubeComponentType === "Dimension" ||
         (cubeComponent.cubeComponentType === "Attribute" &&
          (cubeComponent.IsMandatory || (cubeComponent.assignmentStatus || "").toLowerCase() === "mandatory"));
}

export const checkComponentsHaveCircularDependencies = (components) => {

  const dependentComponents = (components || []).filter(c => c.MappingType !== mappingTypeDefault).map(c => c.CubeComponentCode);

  if (dependentComponents.length === 0) {
    return false;
  }
  
  const referredComponents = [];

  const concatenatedComponents = (components || []).filter(c => c.MappingType === mappingTypeConcat);
 
  for(let i = 0; i < concatenatedComponents.length; i++) {
    const component = concatenatedComponents[i];
    const basicExpression = component.Expression.basicExp;
    for(let j = 0; j < basicExpression.length; j++) {
      const exp = basicExpression[j];
      if (exp.charAt() === '[') {
        const c = exp.slice(1, -1);
        if (referredComponents.indexOf(c) === -1) {
          referredComponents.push(c);
        }        
      }
    }
  }

  if (dependentComponents.some(c => referredComponents.indexOf(c) !== -1)) {
    return true;
  }

  const calculatedComponents = (components || []).filter(c => c.MappingType === mappingTypeCalculated);
  
  for(let i = 0; i < calculatedComponents.length; i++) {
    const component = calculatedComponents[i];
    const calculatedExpression = component.Expression.calcExp;
    for (let j = 0; j < calculatedExpression.length; j++) {
      const expression = calculatedExpression[j];
      if (expression.Item1) {
        const filters = expression.Item1.FiltersGroupAnd || expression.Item1.FiltersGroupOr;
        Object.values(filters).forEach(filter => {
          (filter || []).forEach(f => {
            if (referredComponents.indexOf(f.ColumnName) === -1) {
              referredComponents.push(f.ColumnName);
            }
          })
        });        

      }
    }
  }
   
  return dependentComponents.some(c => referredComponents.indexOf(c) !== -1);

}

export const mappingHasFixedComponent = (mapping) => {

  return mapping.Components.some(c => c.MappingType === mappingTypeFixed);

}

export const createExpressionFunctionToSingle = (condition) => {
  let operators = ['IN','NOT IN']
          const {functionName, componentName, selectDistinct, values, operator } = condition
          switch(functionName){
            case 'Replace':
              if(operators.includes(operator)){
                return `Replace([${componentName}],'${values.replace}','${values.with}') ${operator} (${selectDistinct.map(e => `'${e}'`).join(',')})`;
              }else{
                return `Replace([${componentName}],'${values.replace}','${values.with}') ${operator} '${selectDistinct}'`;
              }       
            case 'Substr':
              if(operators.includes(operator)){
                return `Substring([${componentName}], ${values.start}, ${values.end}) ${operator} (${selectDistinct.map(e => `'${e}'`).join(',')})`
              }else{
                return `Substring([${componentName}], ${values.start}, ${values.end}) ${operator} '${selectDistinct}'`
              }    
            case 'Trim':
              if(values.chars){
                if(operators.includes(operator)){
                  return `Trim('${values.chars}' from [${componentName}]) ${operator} (${selectDistinct.map(e => `'${e}'`).join(',')})`
                }else{
                  return `Trim('${values.chars}' from [${componentName}]) ${operator} '${selectDistinct}'`
                }
                
              }else{
                if(operators.includes(operator)){
                  return `Trim([${componentName}]) ${operator} (${selectDistinct.map(e => `'${e}'`).join(',')})`
                }else{
                    return `Trim([${componentName}])`
                }
                
              }
            case 'LTrim':
              if(operators.includes(operator)){
                return `[dbo].[Left_Trim]([${componentName}],'${values.chars}') ${operator} (${selectDistinct.map(e => `'${e}'`).join(',')})`;
              }else{
                 return `[dbo].[Left_Trim]([${componentName}],'${values.chars}') ${operator} '${selectDistinct}'`;
              }
            case 'RTrim':
              if(operators.includes(operator)){
                return `[dbo].[Right_Trim]([${componentName}],'${values.chars}') ${operator} (${selectDistinct.map(e => `'${e}'`).join(',')})`;
              }else{
                return `[dbo].[Right_Trim]([${componentName}],'${values.chars}') ${operator} '${selectDistinct}'`;
              }        
            case 'LPad':
              if(operators.includes(operator)){
                return `RIGHT('${values.pattern}' + [${componentName}], len('${values.pattern}')) ${operator} (${selectDistinct.map(e => `'${e}'`).join(',')})`;
              }else{
                return `RIGHT('${values.pattern}' + [${componentName}], len('${values.pattern}')) ${operator} '${selectDistinct}'`;
              }             
            case 'RPad':
              if(operators.includes(operator)){
                return `LEFT([${componentName}] + '${values.pattern}', len('${values.pattern}')) ${operator} (${selectDistinct.map(e => `'${e}'`).join(',')})`;
              }else{
                return `LEFT([${componentName}] + '${values.pattern}', len('${values.pattern}')) ${operator} '${selectDistinct}'`;
              }         
            default:
              if(operators.includes(operator)){
                return `[${componentName}] ${operator} (${selectDistinct.map(e => `'${e}'`).join(',')})`;
              }else{
                return `[${componentName}] ${operator} '${selectDistinct}'`;
              }
              
      }
      }

export const createExpressionFunctionToArray = (conditions, logicalOperator = null) => {
   
    let query = '';
    
     for(let i = 0; i < conditions.length; i++){
      if( i > 0){
        query += ` ${conditions[i].logicalOperator || logicalOperator !== null && logicalOperator} ${createExpressionFunctionToSingle(conditions[i])}`
      }else{
        query = createExpressionFunctionToSingle(conditions[0])
      }
    }
    return query
}

export const createExpressionToQuery = (condition) => {
  
  const {functionName, componentName,  values, operator, logicalOperator } = condition
      switch(functionName){
        case 'Replace':
          return `REPLACE([${componentName}],'${values.replace}','${values.with}')`;
        case 'Substr':
        return `SUBSTRING([${componentName}], ${values.start}, ${values.end})`
        case 'Trim':
          if(_.some(values)){
            return `TRIM('${values.chars}' from [${componentName}])`
          }else{
            return `TRIM([${componentName}])`;
          }
        case 'LTrim':
          return `[dbo].[Left_Trim]([${componentName}],'${values.chars}')`;
        case 'RTrim':
          return `[dbo].[Right_Trim]([${componentName}],'${values.chars}')`;
        case 'LPad':
          return `RIGHT('${values.pattern}' + [${componentName}], len('${values.pattern}'))`;
        case 'RPad':
          return `LEFT([${componentName}] + '${values.pattern}', len('${values.pattern}'))`;
        default:
          return `[${componentName}]`;
  }
}

export const  checkSubmitableFunction =(data, keysObjects = [], keysArrays = []) => {
  let r = []
  const fromObject = (cond, expr = []) => {
     let d = []
     for( const [key, value] of Object.entries(cond)){
           if(expr.includes(key)){
              d.push(...fromObject(value))
           }
           else if(!value){
              d.push(key)
           }
        }
     return d
  }


  const fromArray = (data, expr = []) => {
    for(let i = 0;i < data.length; i++){
       for( const [key, value] of Object.entries(_.omit(data[i],['idx','blockIdx','logicalOperator','componentX','componentZ','baseValue']))){  
          if(keysArrays.includes(key)){
             fromArray(data[i][key], expr)
          } 
          else if(keysObjects.includes(key)){
             r.push(...fromObject(value,expr))
          }
          else if(!value){
            r.push(key)
          }
       }
    }
 }

  if(_.isArray(data)){
    fromArray(data,keysObjects)
  }

  if(_.isObject(data)){
    for( const [key, value] of Object.entries(_.omit(data,['idx','blockIdx', 'componentDefault']))){   
      if(keysArrays.includes(key)){
        fromArray(data[key], keysObjects)
      }
      else if(keysObjects.includes(key)){
         r.push(...fromObject(value,keysObjects))
      }
      else if(!value){
         r.push(key)
        }
      }
    }
  return !r.filter(Boolean).length
}
